import { isErrorWithData } from '@/services/helpers';
import { toast } from '@palmetto/palmetto-components';

import { DocumentDocument } from 'types';

/** Result of an upload: the persisted documents plus, for v2 uploads, the job id to watch for variants. */
export interface UploadDocumentsResult {
  documents: DocumentDocument[];
  jobId?: string;
}

type UploadDocumentTrigger = (args: { accountId: string; formData: FormData; invalidateAllTags: boolean }) => {
  unwrap: () => Promise<UploadDocumentsResult>;
};

interface UploadWithToastArgs {
  uploadDocument: UploadDocumentTrigger;
  accountId: string;
  formData: FormData;
  invalidateAllTags: boolean;
  files: FileList | File[];
  /** Override the default loading/success/error messages. */
  messages?: {
    loading?: (fileCount: number) => string;
    success?: (fileCount: number) => string;
    fallbackError?: string;
  };
}

const defaultLoading = (fileCount: number, firstFileName: string) =>
  fileCount === 1 ? `Uploading ${firstFileName}…` : `Uploading ${fileCount} files…`;

const defaultSuccess = (fileCount: number) =>
  fileCount === 1 ? 'Document uploaded' : `${fileCount} documents uploaded`;

/**
 * Dispatch a document upload with toast-based status reporting.
 *
 * Returns the upload promise so callers that need the result (e.g. attaching
 * uploaded documents to a flag) can await it. Pure-upload UIs can ignore the
 * return value and let the toast convey status while the user continues working.
 *
 * Errors surface as a sticky toast (`duration: Infinity`) so failures aren't
 * missed if the user navigated away.
 */
export const uploadDocumentsWithToast = ({
  uploadDocument,
  accountId,
  formData,
  invalidateAllTags,
  files,
  messages,
}: UploadWithToastArgs): Promise<UploadDocumentsResult> => {
  const fileCount = files.length;
  const firstFile = fileCount > 0 ? (files as FileList)[0] : undefined;
  const firstFileName = firstFile?.name ?? '';

  const loadingMessage = messages?.loading?.(fileCount) ?? defaultLoading(fileCount, firstFileName);
  const successMessage = messages?.success?.(fileCount) ?? defaultSuccess(fileCount);
  const fallbackErrorMessage = messages?.fallbackError ?? 'An error occurred while uploading documents';

  const promise = uploadDocument({ accountId, formData, invalidateAllTags }).unwrap();

  toast.async(
    promise,
    {
      loading: loadingMessage,
      success: successMessage,
      error: (e: any) => {
        if (e?.status === 413 || e?.originalStatus === 413) {
          return 'File size too large. Please upload files smaller than 125MB.';
        }
        // v2 mutation surfaces GCS/SSE failures via RTK Query's
        // CUSTOM_ERROR / TIMEOUT_ERROR shapes — fall back to the embedded
        // `error` string so the real cause isn't swallowed by the generic
        // toast message.
        if (e?.status === 'CUSTOM_ERROR' && typeof e?.error === 'string') return e.error;
        if (e?.status === 'TIMEOUT_ERROR') return 'Upload timed out. Please try again.';
        if (isErrorWithData(e)) return e.data.message;
        return fallbackErrorMessage;
      },
    },
    { error: { duration: Infinity } },
  );

  return promise;
};
